import React from 'react';
import { Compass, Flame, Trophy, Zap, TrendingUp, Tag, Hash } from 'lucide-react';
import type { Post } from '../types';
import { usePosts } from '../hooks/usePosts';
import { useMediaQuery } from '../hooks/useMediaQuery';

interface CategoryProps {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function Category({ icon, label, isActive, onClick }: CategoryProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-full transition-colors min-w-[100px] snap-center ${
        isActive 
          ? 'bg-blue-600 text-white' 
          : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/70'
      }`}
    >
      {icon}
      <span className="text-sm font-medium whitespace-nowrap">{label}</span>
    </button>
  );
}

interface TrendingItemProps {
  post: Post;
  rank: number;
  onClick: () => void;
}

function TrendingItem({ post, rank, onClick }: TrendingItemProps) {
  return (
    <div 
      onClick={onClick}
      className="flex items-center gap-3 p-3 rounded-lg bg-gray-900/50 hover:bg-gray-800/50 transition-colors cursor-pointer active:bg-gray-800/70"
    >
      <div className="relative aspect-square w-16 sm:w-20 h-16 sm:h-20 rounded-md overflow-hidden bg-gray-800 shrink-0">
        <img 
          src={post.videoUrl.replace('.mp4', '.jpg')} 
          alt={post.caption}
          className="w-full h-full object-cover"
          onError={(e) => {
            // If image fails to load, show a fallback
            e.currentTarget.src = "https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=300";
          }}
        />
        <div className="absolute bottom-0 right-0 bg-black/70 rounded-tl-md px-1.5 py-0.5">
          <Flame className="w-3 h-3 text-orange-400" />
        </div>
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm line-clamp-2">{post.caption}</p>
        <p className="text-gray-400 text-xs mt-1">@{post.username}</p>
        <div className="flex items-center gap-1 mt-2 flex-wrap">
          {post.hashtags.slice(0, 2).map(tag => (
            <span key={tag} className="text-blue-400 text-xs">#{tag}</span>
          ))}
          {post.hashtags.length > 2 && (
            <span className="text-gray-500 text-xs">+{post.hashtags.length - 2} more</span>
          )}
        </div>
      </div>
      
      <div className="flex flex-col items-center justify-center w-8 shrink-0">
        <span className={`text-lg font-bold ${
          rank === 1 ? 'text-yellow-400' : 
          rank === 2 ? 'text-gray-300' : 
          rank === 3 ? 'text-amber-600' : 'text-gray-500'
        }`}>
          #{rank}
        </span>
        <TrendingUp className="w-4 h-4 text-blue-400 mt-1" />
      </div>
    </div>
  );
}

export function DiscoverView({ onPostSelect }: { onPostSelect: (post: Post) => void }) {
  const [activeCategory, setActiveCategory] = React.useState('trending');
  const { posts } = usePosts();
  const [tags, setTags] = React.useState([
    { name: 'politics', count: 456 },
    { name: 'news', count: 389 },
    { name: 'tech', count: 287 },
    { name: 'gaming', count: 245 },
    { name: 'sports', count: 205 },
    { name: 'music', count: 187 },
    { name: 'fashion', count: 156 },
    { name: 'food', count: 136 },
  ]);
  
  // Scroll to active category on load
  const categoriesRef = React.useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  
  React.useEffect(() => {
    // Scroll active category into view when changed
    if (categoriesRef.current && !isDesktop) {
      const activeButton = categoriesRef.current.querySelector('[data-active="true"]');
      if (activeButton) {
        activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeCategory, isDesktop]);

  return (
    <div className={`fixed inset-0 bg-black overflow-y-auto ${isDesktop ? 'pt-16 ml-[240px]' : 'pt-16 pb-16'} safe-area-top safe-area-bottom overscroll-y-contain`}>
      <div className="px-4 py-3 bg-black sticky top-16 z-10 border-b border-gray-800">
        <div className="flex items-center mb-4">
          <Compass className="w-6 h-6 text-blue-500 mr-2" />
          <h1 className="text-white text-xl font-bold">Discover</h1>
        </div>
        
        <div 
          ref={categoriesRef}
          className={`${isDesktop ? 'flex flex-wrap' : 'overflow-x-auto flex'} gap-2 pb-2 -mx-4 px-4 scrollbar-hide snap-x-mandatory touch-pan-x`}
          style={{ WebkitOverflowScrolling: 'touch' }} // For smooth iOS scrolling
        >
          <Category 
            icon={<Flame className="w-4 h-4" />} 
            label="Trending" 
            isActive={activeCategory === 'trending'} 
            onClick={() => setActiveCategory('trending')} 
          />
          <Category 
            icon={<Zap className="w-4 h-4" />} 
            label="For You" 
            isActive={activeCategory === 'for-you'} 
            onClick={() => setActiveCategory('for-you')} 
          />
          <Category 
            icon={<Trophy className="w-4 h-4" />} 
            label="Popular" 
            isActive={activeCategory === 'popular'} 
            onClick={() => setActiveCategory('popular')} 
          />
          <Category 
            icon={<Tag className="w-4 h-4" />} 
            label="Categories" 
            isActive={activeCategory === 'categories'} 
            onClick={() => setActiveCategory('categories')} 
          />
          {/* Added more categories to demonstrate scrolling */}
          <Category 
            icon={<Hash className="w-4 h-4" />} 
            label="News" 
            isActive={activeCategory === 'news'} 
            onClick={() => setActiveCategory('news')} 
          />
          <Category 
            icon={<Hash className="w-4 h-4" />} 
            label="Sports" 
            isActive={activeCategory === 'sports'} 
            onClick={() => setActiveCategory('sports')} 
          />
        </div>
      </div>
      
      <div className="p-4 max-w-screen-xl mx-auto">
        {/* Trending Section */}
        <div className="mb-6">
          <h2 className="text-white font-bold text-lg mb-3 flex items-center">
            <Flame className="w-5 h-5 text-orange-500 mr-2" />
            Trending Right Now
          </h2>
          <div className="space-y-3">
            {posts.map((post, index) => (
              <TrendingItem 
                key={post.id + index} 
                post={post} 
                rank={index + 1} 
                onClick={() => onPostSelect(post)} 
              />
            ))}
          </div>
        </div>
        
        {/* Popular Tags */}
        <div className="mb-6">
          <h2 className="text-white font-bold text-lg mb-3 flex items-center">
            <Hash className="w-5 h-5 text-blue-500 mr-2" />
            Popular Tags
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {tags.map(tag => (
              <div 
                key={tag.name}
                className="bg-gray-900/50 rounded-lg p-3 hover:bg-gray-800/50 transition-colors cursor-pointer active:bg-gray-800/70"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-blue-400 text-sm font-medium">#{tag.name}</span>
                  <span className="text-gray-400 text-xs">{tag.count}K</span>
                </div>
                <div className="w-full bg-gray-800 h-1 rounded-full overflow-hidden">
                  <div 
                    className="bg-blue-500 h-full rounded-full" 
                    style={{ width: `${Math.min(100, (tag.count / 500) * 100)}%` }} 
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Recommended Accounts */}
        <div className="mb-6">
          <h2 className="text-white font-bold text-lg mb-3">Recommended Accounts</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {['cnn', 'bloomberg', 'reuters', 'bbcnews', 'washingtonpost'].map(account => (
              <div 
                key={account}
                className="bg-gray-900/50 rounded-lg p-3 flex flex-col items-center hover:bg-gray-800/50 transition-colors cursor-pointer active:bg-gray-800/70"
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden mb-2 bg-gray-800">
                  <img 
                    src={account === 'cnn' 
                      ? 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/66/CNN_International_logo.svg/2048px-CNN_International_logo.svg.png'
                      : account === 'bloomberg'
                      ? 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/56/Bloomberg_logo.svg/512px-Bloomberg_logo.svg.png'
                      : account === 'reuters'
                      ? 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/83/Reuters_logo.svg/512px-Reuters_logo.svg.png'
                      : account === 'washingtonpost'
                      ? 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a0/The_Washington_Post_masthead.svg/2560px-The_Washington_Post_masthead.svg.png'
                      : 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/41/BBC_Logo_2021.svg/512px-BBC_Logo_2021.svg.png'
                    } 
                    alt={account}
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-white text-sm font-medium">@{account}</p>
                <p className="text-gray-400 text-xs mt-1">News & Media</p>
                <button className="mt-2 bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1 rounded-full transition-colors active:bg-blue-700">
                  Follow
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}